import 'react-perfect-scrollbar/dist/css/styles.css'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import 'react-quill/dist/quill.snow.css'
import 'nprogress/nprogress.css'
import './index.css'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { Provider as ReduxProvider } from 'react-redux'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import StyledEngineProvider from '@mui/material/StyledEngineProvider'
import * as Sentry from '@sentry/react'
import { SnackbarProvider } from 'notistack'
import App from './App'
import { SettingsProvider } from './contexts/SettingsContext'
import ConfirmContextProvider from './contexts/ConfirmContextProvider'
import store from './store'
import { appConfig, authenticationProviderType, awsConfig, keycloakConfig, torqCloudConfig } from './config'
import { AuthProvider, CognitoAuthenticationManager, IAuthenticationManager, IUser, KeycloakAuthenticationManager } from 'torqcloud-ui'
import { OrganizationProvider } from './contexts/OrganizationContext'
import { ApiClientContextProvider } from './contexts/ApiClientContext'
import { NodesProvider } from './contexts/NodesContext'
import ErrorBoundary from './components/ErrorBoundary'

Sentry.init({
  dsn: appConfig.sentryDsn,
  environment: appConfig.sentryEnvironment,
  release: appConfig.uiVersion,
})

let authenticationManager: IAuthenticationManager

switch (authenticationProviderType) {
  case 'keycloak':
    authenticationManager = new KeycloakAuthenticationManager({
      ...keycloakConfig,
      redirect_uri: window.location.origin,
    })
    break
  case 'cognito': {
    const config = {
      Auth: {
        region: awsConfig.region,
        userPoolId: awsConfig.userPoolId,
        userPoolWebClientId: awsConfig.appClientId,
        cookieStorage: {
          domain: awsConfig.cookieDomain,
          secure: import.meta.env.VITE_ENV !== 'development',
        },
      },
    }

    authenticationManager = new CognitoAuthenticationManager(config)
    break
  }
  default:
    throw new Error(`Unrecognized Authentication Provider: ${authenticationProviderType}`)
}

const container = document.getElementById('root') as HTMLElement
const root = createRoot(container)

let user: IUser | undefined
try {
  user = await authenticationManager.currentAuthenticatedUser()
} catch (error) {
  console.warn(`Failed to get current session: ${error}`)
}

root.render(
  <StrictMode>
    <HelmetProvider>
      <ReduxProvider store={store}>
        <StyledEngineProvider injectFirst>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SettingsProvider>
              <BrowserRouter>
                <ErrorBoundary boundaryName="app" currentUser={user}>
                  <AuthProvider authenticationManager={authenticationManager} torqCloudApiUrl={torqCloudConfig.url}>
                    <OrganizationProvider>
                      <ApiClientContextProvider>
                        <SnackbarProvider>
                          <ConfirmContextProvider>
                            <NodesProvider>
                              <App />
                            </NodesProvider>
                          </ConfirmContextProvider>
                        </SnackbarProvider>
                      </ApiClientContextProvider>
                    </OrganizationProvider>
                  </AuthProvider>
                </ErrorBoundary>
              </BrowserRouter>
            </SettingsProvider>
          </LocalizationProvider>
        </StyledEngineProvider>
      </ReduxProvider>
    </HelmetProvider>
  </StrictMode>
)
